import { QueryClient, QueryKey } from '@tanstack/react-query';
import { parse, serialize } from 'cookie';
import { type GetServerSidePropsContext, type GetServerSidePropsResult } from 'next';

import { getCurrentUser } from '@api/auth/api';
import { CurrentUser } from '@api/auth/types';
import { getCategoriesTree } from '@api/catalog/categories/api';
import { getPagination } from '@api/common/helpers';
import { CommonResponse, FetchError } from '@api/common/types';
import { getMenu } from '@api/content/api';
import { CompareData } from '@api/customers';
import { getComparedOffers, getCustomerProfile, getFavoriteOffers } from '@api/customers/api';
import { APIClient } from '@api/index';
import getIpAddress from '@api/ip';

import { TokenDataProps } from '@context/auth';

import { CLIENT_IP_COOKIE_NAME } from '@scripts/clientIp';
import { defaultCookieOptions, HttpCode, MAX_AGE_DAY } from '@scripts/constants';
import { getHost } from '@scripts/helpers';
import { parseGuestCompareCookie } from '@scripts/helpers/parseGuestCompareCookie';
import { getRequestOrigin } from '@scripts/seo/getRequestOrigin';

import { ISSRPageProps } from './types';

/** Fields used only during SSR callbacks — never serialized into __NEXT_DATA__. */
const SENSITIVE_PAGE_PROP_KEYS = ['accessToken', 'expiresAt', 'refreshToken', 'ip'] as const;

type SensitivePagePropKey = (typeof SENSITIVE_PAGE_PROP_KEYS)[number];

const omitSensitivePageProps = <T extends Record<string, unknown>>(props: T): Omit<T, SensitivePagePropKey> => {
    const next = { ...props };

    for (const key of SENSITIVE_PAGE_PROP_KEYS) {
        delete next[key];
    }

    return next;
};

const getRequestPath = (context: GetServerSidePropsContext): string => {
    const raw = context.resolvedUrl ?? context.req.url ?? '';
    const withoutQuery = raw.split('?')[0];
    const nextDataMatch = withoutQuery.match(/^\/_next\/data\/[^/]+(\/.+?)(?:\.json)?$/);

    if (nextDataMatch) {
        return nextDataMatch[1];
    }

    return withoutQuery;
};

const isUnauthorizedPrefetchError = (error: unknown): error is FetchError =>
    error instanceof FetchError && error.status === HttpCode.UNAUTHORIZED;

const prefetchQuery = async <TResult>(
    queryClient: QueryClient,
    queryKey: QueryKey | string,
    queryFn: () => Promise<TResult>
): Promise<TResult | undefined> => {
    try {
        return await queryClient.fetchQuery({
            queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
            queryFn,
        });
    } catch {
        return undefined;
    }
};

/** Fetch for page props only — skip queryClient so dehydrate does not duplicate the payload. */
const fetchForProps = async <TResult>(queryFn: () => Promise<TResult>): Promise<TResult | undefined> => {
    try {
        return await queryFn();
    } catch {
        return undefined;
    }
};

const prefetchQueryStrict = <TResult>(
    queryClient: QueryClient,
    queryKey: QueryKey | string,
    queryFn: () => Promise<TResult>
): Promise<TResult> =>
    queryClient.fetchQuery({
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        queryFn,
    });

/**
 * Получить общие для всех страниц данные
 */
export const getCommonProps = async <TProps extends ISSRPageProps>(
    context: GetServerSidePropsContext,
    fn: (params: any, queryClient: QueryClient, apiClient: APIClient) => Promise<GetServerSidePropsResult<TProps>>
) => {
    try {
        const { req, res } = context;

        const ip = getIpAddress(req);

        const cookies = req.headers.cookie ? parse(req.headers.cookie) : {};

        const currentCity = cookies.city;
        const guestCompareData = parseGuestCompareCookie(cookies.compareProducts);

        let token: TokenDataProps | undefined = cookies
            ? {
                  accessToken: cookies.access_token,
                  expiresAt: cookies.expires_at,
                  refreshToken: cookies.refresh_token,
              }
            : undefined;

        const setToken = (newTokens: TokenDataProps) => {
            token = newTokens;
            res.setHeader('Set-Cookie', [
                serialize('access_token', token.accessToken || '', {
                    maxAge: 0,
                    ...defaultCookieOptions,
                }),
                serialize('refresh_token', cookies.refresh_token || '', {
                    maxAge: 0,
                    ...defaultCookieOptions,
                }),
                serialize('expires_at', token.expiresAt || '', {
                    maxAge: 0,
                    ...defaultCookieOptions,
                }),
            ]);
        };

        const apiClient = new APIClient(`${getHost()}/api/v1/`, token, setToken, ip as string);

        const queryClient = new QueryClient();

        const commonParams = {
            pagination: getPagination(-1),
            include: ['items'],
        };

        const comparedParams = {
            sort: 'id',
            pagination: getPagination(-1),
        };

        const isLkPage = getRequestPath(context).startsWith('/lk');

        const { key: footerMainKey, fetch: footerMainFetch } = getMenu(apiClient)({
            filter: { code: 'footer_main' },
            ...commonParams,
        });

        const { key: headerMainKey, fetch: headerMainFetch } = getMenu(apiClient)({
            filter: { code: 'header_main' },
            ...commonParams,
        });

        const { key: headerHelpKey, fetch: headerHelpFetch } = getMenu(apiClient)({
            filter: { code: 'header_help' },
            ...commonParams,
        });

        // Mega-menu tree goes to props (Header) only — fetchQuery+dehydrate would serialize ~41 KB twice.
        const { fetch: categoriesTreeFetch } = getCategoriesTree(apiClient);

        const [footerData, headerMainData, headerHelpData, categoriesTree] = await Promise.all([
            prefetchQuery(queryClient, footerMainKey, footerMainFetch),
            prefetchQuery(queryClient, headerMainKey, headerMainFetch),
            prefetchQuery(queryClient, headerHelpKey, headerHelpFetch),
            fetchForProps(categoriesTreeFetch),
        ]);

        let currentUser: CommonResponse<CurrentUser> | undefined;
        let comparedOffersData: CompareData[] = [];
        let lkCriticalPrefetchFailed = false;

        if (token?.accessToken) {
            const { key: currentUserKey, fetch: currentUserFetch } = getCurrentUser(apiClient);

            if (isLkPage) {
                try {
                    currentUser = await prefetchQueryStrict(queryClient, currentUserKey, currentUserFetch);
                } catch (error) {
                    if (!isUnauthorizedPrefetchError(error)) {
                        lkCriticalPrefetchFailed = true;
                        res.statusCode = HttpCode.INTERNAL_SERVER_ERROR;
                    }
                }
            } else {
                currentUser = await prefetchQuery(queryClient, currentUserKey, currentUserFetch);
            }

            if (!lkCriticalPrefetchFailed && currentUser?.data) {
                if (isLkPage) {
                    const { key: customerProfileKey, fetch: customerProfileFetch } = getCustomerProfile(apiClient)([
                        'children',
                    ]);

                    try {
                        await prefetchQueryStrict(queryClient, customerProfileKey, customerProfileFetch);
                    } catch (error) {
                        if (!isUnauthorizedPrefetchError(error)) {
                            lkCriticalPrefetchFailed = true;
                            res.statusCode = HttpCode.INTERNAL_SERVER_ERROR;
                        }
                    }
                }

                if (!lkCriticalPrefetchFailed) {
                    const { key: comparedOffersKey, fetch: comparedOffersFetch } = getComparedOffers(apiClient)({
                        ...comparedParams,
                    });

                    const comparedOffersResponse = await prefetchQuery(
                        queryClient,
                        comparedOffersKey,
                        comparedOffersFetch
                    );

                    comparedOffersData = comparedOffersResponse?.data ?? [];

                    const { key: favoritesKey, fetch: favoritesFetch } = getFavoriteOffers(apiClient)(comparedParams);

                    await prefetchQuery(queryClient, favoritesKey, favoritesFetch);
                }
            }
        }

        const requestOrigin = getRequestOrigin(req.headers);
        const clientIp = String(ip || '')
            .split(',')[0]
            .trim();

        // accessToken/ip stay available to page gSSP callbacks, then stripped before HTML serialization.
        const baseProps = {
            lkCriticalPrefetchFailed,
            guestCompareData,
            requestOrigin,
            isAuthenticated: Boolean(token?.accessToken),
            ...(!!token?.accessToken && { accessToken: token?.accessToken }),
            ...(!!token?.expiresAt && { expiresAt: token?.expiresAt }),
            ...(!!currentCity && { currentCity: currentCity }),
            ip,
            footerData: footerData?.data ?? [],
            headerMainItems: headerMainData?.data?.[0]?.items ?? [],
            headerHelpItems: headerHelpData?.data?.[0]?.items ?? [],
            categoriesTree: categoriesTree?.data ?? [],
            ...(currentUser?.data && {
                userData: currentUser.data,
                comparedOffersData,
            }),
        };

        const result = await fn(baseProps, queryClient, apiClient);

        if (!('props' in result)) {
            return result;
        }

        const resolvedProps = await Promise.resolve(result.props);

        // Readable by the browser for APIClient; not embedded in page HTML. Append after SSR work
        // so token refresh Set-Cookie headers are not overwritten.
        const clientIpCookie = serialize(CLIENT_IP_COOKIE_NAME, clientIp || '0.0.0.0', {
            maxAge: MAX_AGE_DAY,
            path: '/',
            secure: defaultCookieOptions.secure,
            httpOnly: false,
            sameSite: 'lax',
        });
        const existingSetCookie = res.getHeader('Set-Cookie');
        if (!existingSetCookie) {
            res.setHeader('Set-Cookie', clientIpCookie);
        } else {
            const list = Array.isArray(existingSetCookie) ? existingSetCookie.map(String) : [String(existingSetCookie)];
            res.setHeader('Set-Cookie', [...list, clientIpCookie]);
        }

        return {
            ...result,
            props: {
                ...omitSensitivePageProps(resolvedProps as Record<string, unknown>),
                isAuthenticated: Boolean(token?.accessToken),
                requestOrigin,
            },
        };
    } catch (err) {
        console.error('SSR error:', err);

        return Promise.resolve({
            props: {
                statusCode: 500,
                err: JSON.stringify(err),
            },
            notFound: true,
        } as never as GetServerSidePropsResult<TProps>);
    }
};
